import type { ContextTreeAccount } from '@/hooks/useContextTree';
import { formatFeedBadge } from '@/lib/feedLabels';

/**
 * Reine Suchfunktionen fuer den FeedSwitcher-Baum (Task 5). Kein React, kein
 * i18n, keine Supabase-Abhaengigkeit — nur Strings und der Baum aus
 * `useContextTree`.
 */

/**
 * Diakritikfeste Normalisierung: `Läderach` -> `laderach`.
 *
 * `NFD` zerlegt jedes Zeichen mit Akzent/Umlaut in Basiszeichen + Kombinations-
 * marke (U+0300 bis U+036F), die Regex entfernt genau diese Marken. Ohne das
 * findet eine Suche nach "laderach" den Shopnamen "House of Läderach" nie —
 * genau der Review-Blocker, den dieser Schritt schliesst.
 */
export function normalizeForSearch(s: string): string {
  return s.normalize('NFD').replace(/[̀-ͯ]/g, '').toLowerCase();
}

export interface FlatHit {
  kind: 'feed' | 'mc';
  accountId: string;
  mcId: string;
  feedId?: string;
  haystack: string;
}

/** Ob `haystack` die (diakritikfrei, gross-/kleinschreibungs-unabhaengig normalisierte) `query` enthaelt. */
export function matchesQuery(haystack: string, query: string): boolean {
  return normalizeForSearch(haystack).includes(normalizeForSearch(query.trim()));
}

/**
 * Flacht den Baum aus `useContextTree` zu Such-Treffern ab: ein `mc`-Treffer
 * je Merchant Center, ein `feed`-Treffer je Feed darin. Traegt bewusst nur
 * IDs + einen zusammengesetzten Haystack, keine Anzeigedaten — `FeedSwitcherList`
 * schlaegt die Anzeige ueber die IDs im vollen Baum nach, sobald ein Treffer
 * feststeht.
 *
 * MCs mit `accessStatus !== 'active'` werden komplett uebersprungen (I3,
 * Fix-Runde 2) — im Baum-Modus ist eine widerrufene MC-Zeile `disabled` und
 * ihre Feeds sind gar nicht erst aufklappbar (spec §4.1 "nicht waehlbar"),
 * aber die Suche kannte diese Regel nicht: sie flachte den Baum blind ab und
 * machte Feeds unter einer widerrufenen MC ueber einen Suchtreffer waehlbar.
 */
export function flattenTree(accounts: ContextTreeAccount[]): FlatHit[] {
  const hits: FlatHit[] = [];

  for (const acc of accounts) {
    for (const mc of acc.merchantCenters) {
      if (mc.accessStatus !== 'active') continue;

      const mcHaystack = [mc.name, acc.displayName, acc.email].filter(Boolean).join(' ');
      hits.push({ kind: 'mc', accountId: acc.id, mcId: mc.id, haystack: mcHaystack });

      for (const feed of mc.feeds) {
        const badge = formatFeedBadge(feed.targetCountry, feed.contentLanguage);
        const haystack = [feed.displayName, mc.name, acc.displayName, acc.email, badge]
          .filter(Boolean)
          .join(' ');
        hits.push({ kind: 'feed', accountId: acc.id, mcId: mc.id, feedId: feed.id, haystack });
      }
    }
  }

  return hits;
}
